import type { JSX } from 'react';
import { Pill } from '@/components/ui/pill';
import { inviteStage } from '@/lib/labels';
import { formatDate } from '@/lib/dates';
import { stageAgeLabel, stageSince } from '@/lib/moneyPages';
import type { OutreachInvite } from '@/api/types';

type StagePillInvite = Pick<OutreachInvite, 'status'> &
  Partial<Pick<OutreachInvite, 'deletedAt' | 'releasedAt' | 'createdAt' | 'convertedAt'>> & {
    verifiedAt?: string | null;
  };

/**
 * The invite's place in the outreacher flow (Replied, Joined, Posted, or
 * off-path: Expired, Not paid, Hidden, Manual tracking), with a tooltip that
 * says what it means. Use this instead of rendering `invite.status`.
 *
 * `now` adds how long it has been in that stage ("Replied · 9d"), when the
 * row says when it got there. Without a timestamp the age is left off
 * rather than guessed.
 */
export function InviteStagePill({
  invite,
  teamTracks,
  now,
  className,
}: {
  invite: StagePillInvite;
  /** The team's clippingResolveRequired setting, when known. */
  teamTracks?: boolean;
  /** Show the stage's age, measured to this time. */
  now?: Date;
  className?: string;
}): JSX.Element {
  const stage = inviteStage(invite, { teamTracks });
  const ageInput = invite.createdAt ? { ...invite, createdAt: invite.createdAt } : null;
  const age = now && ageInput ? stageAgeLabel(stage.key, ageInput, now) : null;
  const since = age && ageInput ? stageSince(stage.key, ageInput) : null;
  return (
    <Pill
      tone={stage.tone}
      title={since ? `${stage.hint} Since ${formatDate(since)}.` : stage.hint}
      className={className}
    >
      {age ? `${stage.label} · ${age}` : stage.label}
    </Pill>
  );
}
